<i18n src="./resources/locales.json"></i18n>

<template>
  <div class="toolbar">
    <div v-show="showLinks" ref="links" class="links">
      <!-- div v-once ref="linksLabel" class="links-label">{{ $t('labels.links') }}:</div -->
      <div ref="linksContainer" class="links-container">
        <span v-for="(link, index) in interactionLinks" v-show="index < visibleLinks" :key="index" class="pl-1 link">
          <a :href="link.url" target="_blank">
            {{ link.name }}
          </a>
        </span>
      </div>
      <div ref="showAllLinksBtn" class="show-all-links">
        <v-menu offset-y>
          <template v-slot:activator="{ on: menu }">
            <v-tooltip bottom>
              <template v-slot:activator="{ on: tooltip }">
                <a v-show="overflowedLinks" v-on="{ ...tooltip, ...menu }"> +{{ overflowedLinks }} </a>
              </template>
              <span v-once>{{ $t('labels.links') }}</span>
            </v-tooltip>
          </template>
          <v-list class="pa-0">
            <a
              v-for="(link, index) in filteredInteractionLinks"
              :key="index"
              :href="link.url"
              target="_blank"
              class="list-item-link"
            >
              <v-list-item @click="() => {}">
                <v-list-item-title class="menu-link">{{ link.name }}</v-list-item-title>
              </v-list-item>
            </a>
          </v-list>
        </v-menu>
      </div>
    </div>
    <div class="d-flex actions-info" :class="interaction.isEmail ? 'interaction-email' : ''">
      <div v-if="actions.length" class="d-flex">
        <v-tooltip v-for="item in actions" :key="item.label" bottom>
          <template v-slot:activator="{ on }">
            <v-btn
              v-show="!item.hidden"
              class="controls"
              :class="item.label === 'buttons.discard' ? 'discard' : ''"
              :disabled="isOfflineModeEnabled || item.disabled"
              :loading="isWaitingFor(item.waitsFor)"
              icon
              small
              v-on="on"
              @click="buttonAction(item.action)"
            >
              <v-icon>{{ item.icon }}</v-icon>
            </v-btn>
          </template>
          <span>{{ $t(item.label) }}</span>
        </v-tooltip>
      </div>
      <div v-if="primaryAction">
        <v-btn
          :disabled="isPrimaryActionDisabled"
          :loading="isWaitingFor(primaryAction.waitsFor)"
          class="end-chat-btn large"
          color="primary"
          raised
          @click="buttonAction(primaryAction.action)"
        >
          <v-icon small left>{{ primaryAction.icon }}</v-icon>
          {{ $t(primaryAction.label) }}
        </v-btn>
      </div>
    </div>

    <!-- Dialogs -->
    <EcsContactsSelectionDialog ref="contactSelectionDialog" />
    <EcsConfirmationDialog
      ref="addToDncConfirmationDialog"
      :title="$t('dialogs.addToDNCListDialog.title')"
      :message="$t('dialogs.addToDNCListDialog.content')"
      :confirm-button-text="$t('dialogs.addToDNCListDialog.acceptBtn')"
      :cancel-button-text="$t('dialogs.addToDNCListDialog.cancelBtn')"
    />
    <EcsConfirmationDialog
      ref="removeFromDncConfirmationDialog"
      :title="$t('dialogs.removeFromDNCListDialog.title')"
      :message="$t('dialogs.removeFromDNCListDialog.content')"
      :confirm-button-text="$t('dialogs.removeFromDNCListDialog.acceptBtn')"
      :cancel-button-text="$t('dialogs.removeFromDNCListDialog.cancelBtn')"
    />
    <EcsGroupChatSetupDialog ref="groupChatSetupDialog" />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { INTERACTION_STATE } from '@/constants/interaction/constants';
import EcsContactsSelectionDialog from '@/components/ecs-contacts-selection-dialog/EcsContactsSelectionDialog';
import EcsConfirmationDialog from '@/components/ecs-confirmation-dialog/EcsConfirmationDialog';
import EcsGroupChatSetupDialog from '@/components/ecs-group-chat-setup-dialog/EcsGroupChatSetupDialog';
import EcsInteractionActionsMixin from '@/mixins/ecs-interaction-mixin/EcsInteractionActionsMixin';

export default {
  name: 'InteractionToolbar',

  components: { EcsGroupChatSetupDialog, EcsContactsSelectionDialog, EcsConfirmationDialog },

  mixins: [EcsInteractionActionsMixin],

  props: {
    interaction: {
      type: Object,
      required: true
    }
  },

  data() {
    return {
      visibleLinks: 0,
      resizeObserver: null
    };
  },

  computed: {
    ...mapGetters('EVENT_TIMING', ['isWaitingForEvent']),

    ...mapGetters('SETTINGS', ['isOfflineModeEnabled']),

    interactionLinks() {
      return this.interaction.links || [];
    },

    hasLinks() {
      return this.interaction.links && this.interaction.links.length > 0;
    },

    actions() {
      let result;

      if (this.interaction.interactionStateId === INTERACTION_STATE.offered) {
        result = [];
      } else if (this.interaction.isHistoric) {
        result = this.getActionsOnHistoric();
      } else {
        result = this.getActionsOnActive();
      }

      return result;
    },

    primaryAction() {
      let result;

      if (this.interaction.isHistoric) {
        result = null;
      } else if (this.interaction.isOnWrapUp) {
        result = this.primaryOptions.complete;
      } else if (this.interaction.isEmail) {
        const email = this.getEmailByInteractionId(this.interaction.interactionId); // NOTE: Review on detail
        result = email && email.isReplying ? this.primaryOptions.send : this.primaryOptions.reply;
      } else {
        result = this.primaryOptions.end;
      }

      return result;
    },

    overflowedLinks() {
      return this.interactionLinks.length - this.visibleLinks;
    },

    showLinks() {
      return this.hasLinks && !this.interaction.isConsultReceiver;
    },

    isPrimaryActionDisabled() {
      return this.isOfflineModeEnabled || this.primaryAction.disabled || this.isWaitingFor(this.primaryAction.waitsFor);
    },

    filteredInteractionLinks() {
      return this.interactionLinks.slice(-this.overflowedLinks);
    }
  },

  mounted() {
    this.calcOverflowedLinks();
    this.resizeObserver = new ResizeObserver(this.onResize);
    this.resizeObserver.observe(this.$refs.links);
  },

  beforeDestroy() {
    this.resizeObserver.unobserve(this.$refs.links);
    delete this.resizeObserver;
  },

  methods: {
    onResize() {
      this.calcOverflowedLinks();
    },

    async calcOverflowedLinks() {
      const container = this.$refs.linksContainer;
      if (!container) {
        return;
      }
      this.visibleLinks = this.interactionLinks.length;
      await this.$nextTick();

      const links = container.querySelectorAll('.link');

      let sum = 0;
      this.visibleLinks = 0;

      for (const link of links) {
        sum += link.getBoundingClientRect().width;
        if (sum <= container.getBoundingClientRect().width) {
          ++this.visibleLinks;
        } else {
          break;
        }
      }
    },

    isWaitingFor(events) {
      let result;
      if (!events) {
        return result;
      }
      return this.isWaitingForEvent({
        interactionId: this.interaction.interactionId,
        constraintNames: events
      });
    },

    buttonAction(action) {
      this.$root.$emit('close-dialpad');
      action();
    }
  }
};
</script>

<style scoped>
.actions-info {
  justify-self: end;
}
.controls .v-icon {
  font-size: 16px;
}
.controls:not(:first-child) {
  margin-left: 8px;
}
.end-chat-btn.large {
  width: auto !important;
  font-size: 11px;
  height: 28px !important;
}
.end-chat-btn {
  margin-left: 4px;
}
.links {
  grid-row: 1;
  grid-column: 1;
  align-self: center;
  justify-self: start;
  font-size: 10px;
  overflow: auto;
  width: 100%;
  display: grid;
  grid-template-columns: min-content auto min-content;
}
.links-label {
  padding-right: 4px;
}
.links-container {
  overflow-x: hidden;
}
.links-container.show-scroll {
  overflow-x: auto;
}
.show-all-links {
  padding-left: 4px;
}
.toolbar {
  display: grid;
  grid-template-columns: auto min-content;
  grid-template-rows: auto;
  align-items: start;
  padding: 4px 8px;
  width: 100%;
  z-index: 1;
}
.toolbar > div {
  text-overflow: ellipsis;
  white-space: nowrap;
}
.link-icon {
  font-size: 20px !important;
  display: block;
  line-height: 22px;
}
.menu-link {
  font-size: 13px;
  color: var(--v-primary-base) !important;
}
</style>

<style>
.toolbar .controls .v-progress-circular {
  width: 16px !important;
  height: 16px !important;
}
.list-item-link + .list-item-link {
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}
a.list-item-link {
  text-decoration: none;
}

.d-flex.interaction-email {
  justify-self: unset;
  justify-content: end;
}

.d-flex.interaction-email .d-flex {
  width: 100%;
}
.d-flex.interaction-email .discard {
  margin-right: auto;
}
</style>
